import DialogController from "./dialog_controller"

// Tab-scoped preference survives both Turbo visits and posting pages' full navigation.
let collapsed = false
let menuScrollTop = 0
const railStorageKey = "car-nexa:navigation:collapsed"
const scrollStorageKey = "car-nexa:navigation:scroll-top"

export default class extends DialogController {
  static targets = ["collapse", "tooltip", "links"]

  connect() {
    this.element.classList.remove("is-navigation-ready")
    try {
      const saved = window.sessionStorage.getItem(railStorageKey)
      if (saved === "true" || saved === "false") collapsed = saved === "true"
      const savedScroll = window.sessionStorage.getItem(scrollStorageKey)
      if (savedScroll !== null && Number.isFinite(Number(savedScroll)) && Number(savedScroll) >= 0) menuScrollTop = Number(savedScroll)
    } catch { /* In-memory preference still works when storage is unavailable. */ }
    this.restoringScroll = true
    super.connect()
    this.motion = matchMedia("(prefers-reduced-motion: reduce)")
    this.motionChanged = () => {
      if (!this.motion.matches) return
      if (this.closing) this.finishClose()
      else this.cancelAnimation()
    }
    this.motion.addEventListener("change", this.motionChanged)
    this.restoreMenuScroll()
  }

  restoreMenuScroll() {
    this.scrollFrame = requestAnimationFrame(() => {
      // Accordion controllers reveal their rail links before this frame runs.
      if (this.persistent && this.hasLinksTarget) this.linksTarget.scrollTop = menuScrollTop
      this.restoringScroll = false
      this.scrollFrame = undefined
      // Paint the restored width first; only deliberate later changes animate.
      this.readyFrame = requestAnimationFrame(() => {
        this.element.classList.add("is-navigation-ready")
        this.readyFrame = undefined
      })
    })
  }

  prepareRender(event) {
    const shell = event.detail.newBody.querySelector(".operator-shell")
    if (!shell) return
    shell.classList.remove("is-navigation-ready")
    shell.classList.toggle("is-collapsed", matchMedia(this.persistentMediaValue).matches && collapsed)
  }

  rememberScroll() {
    if (!this.persistent || !this.hasLinksTarget || this.restoringScroll) return
    menuScrollTop = this.linksTarget.scrollTop
    try { window.sessionStorage.setItem(scrollStorageKey, String(menuScrollTop)) }
    catch { /* In-memory scroll restoration still works during Turbo visits. */ }
  }

  syncMedia() {
    this.cancelAnimation()
    this.closing = false
    super.syncMedia()
    this.element.classList.remove("is-menu-open")
    this.syncRail()
  }

  syncRail() {
    this.element.classList.toggle("is-collapsed", this.persistent && collapsed)
    this.collapseTarget.setAttribute("aria-expanded", String(!this.persistent || !collapsed))
    this.collapseTarget.setAttribute("aria-label", this.persistent ? (collapsed ? "メニューを展開する" : "メニューを折りたたむ") : "メニューを閉じる")
    this.hideTooltip()
    this.dispatch("rail-change", { detail: { collapsed: this.persistent && collapsed } })
  }

  toggle() {
    if (!this.persistent) return this.close()
    collapsed = !collapsed
    this.rememberRail()
    this.syncRail()
  }

  rememberRail() {
    try { window.sessionStorage.setItem(railStorageKey, String(collapsed)) }
    catch { /* Storage restrictions must not prevent navigation. */ }
  }

  expandGroup(event) {
    if (!this.persistent || !collapsed) return
    collapsed = false
    this.rememberRail()
    this.syncRail()
    // An already-open group only needs its labels revealed.
    if (event.currentTarget.parentElement.open) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  open(event) {
    if (this.persistent || this.dialog.open) return
    super.open(event)
    this.element.classList.add("is-menu-open")
    this.collapseTarget.focus({ preventScroll: true })
    this.animateDrawer(true)
  }

  close() {
    if (this.persistent || !this.dialog.open || this.closing) return
    this.closing = true
    this.animateDrawer(false, () => this.finishClose())
  }

  cancel(event) {
    event.preventDefault()
    if (!this.persistent) this.close()
  }

  finishClose() {
    this.cancelAnimation()
    this.closing = false
    super.close()
  }

  closed() {
    if (this.dialog.open) return
    this.element.classList.remove("is-menu-open")
    super.closed()
  }

  animateDrawer(open, complete) {
    const style = getComputedStyle(this.dialog)
    const start = this.animation ? { transform: style.transform, opacity: style.opacity } :
      { transform: open ? "translateX(-24px)" : "translateX(0)", opacity: open ? 0 : 1 }
    this.cancelAnimation()
    if (this.motion?.matches || !this.dialog.animate) { complete?.(); return }
    const animation = this.dialog.animate([start, { transform: open ? "translateX(0)" : "translateX(-24px)", opacity: open ? 1 : 0 }],
      { duration: open ? 220 : 180, easing: "cubic-bezier(.22,.7,.2,1)" })
    this.animation = animation
    animation.onfinish = () => {
      if (this.animation !== animation) return
      this.animation = null
      complete?.()
    }
  }

  cancelAnimation() {
    if (!this.animation) return
    this.animation.onfinish = null
    this.animation.cancel()
    this.animation = null
  }

  showTooltip(event) {
    if (!this.persistent || !collapsed) return
    this.tooltipTarget.textContent = event.currentTarget.getAttribute("aria-label")
    this.tooltipTarget.hidden = false
    const rect = event.currentTarget.getBoundingClientRect()
    this.tooltipTarget.style.left = `${rect.right + 12}px`
    this.tooltipTarget.style.top = `${Math.max(8, Math.min(innerHeight - this.tooltipTarget.offsetHeight - 8, rect.top))}px`
  }

  hideTooltip() { if (this.hasTooltipTarget) this.tooltipTarget.hidden = true }

  otherOpened(event) {
    if (event.target === this.element || this.persistent) return
    this.returnFocus = null
    this.finishClose()
  }

  prepareCache() {
    // Turbo caches the live document; a cancelled visit may leave it on screen.
    if (!this.persistent) return this.discard()
    this.rememberScroll()
    this.hideTooltip()
  }

  discard() {
    this.rememberScroll()
    this.restoringScroll = true // Hiding the cached menu must not save a reset scroll position.
    if (this.scrollFrame !== undefined) cancelAnimationFrame(this.scrollFrame)
    this.scrollFrame = undefined
    if (this.readyFrame !== undefined) cancelAnimationFrame(this.readyFrame)
    this.readyFrame = undefined
    this.element.classList.remove("is-navigation-ready")
    this.cancelAnimation()
    this.closing = false
    this.hideTooltip()
    this.element.classList.remove("is-menu-open")
    super.discard()
  }

  disconnect() {
    this.motion?.removeEventListener("change", this.motionChanged)
    super.disconnect()
  }
}
